import React, { ComponentProps } from 'react';
import * as RadixDropdownMenu from '@radix-ui/react-dropdown-menu';
import MenuItem, { MenuItemProps } from './Menu/MenuItem';
import MenuContent from './Menu/MenuContent';
import MenuDrawer from './Menu/MenuDrawer';
import { useMediaQuery } from '@/hooks/useMediaQuery';
import { ScreenSizeQueries } from '@/constants/breakpoints';
import { MenuStyle } from './Menu/MenuStyle';

export type TDropdownMenuButtonProps = { isOpen: boolean };
export type TDropdownMenuItem = MenuItemProps | { separator: true };

type TDropdownMenuProps = {
  mobileFooterContent?: ComponentProps<typeof MenuContent>['mobileFooterContent'];
  items?: TDropdownMenuItem[];
  align?: ComponentProps<typeof RadixDropdownMenu.Content>['align'];
  button: (props: TDropdownMenuButtonProps) => JSX.Element;
  isModal?: boolean;
  menuStyle?: MenuStyle;
  menuClassName?: string;
};

const MENU_STYLE = 'standalone';

const DropdownMenu = ({
  button,
  items,
  align = 'start',
  menuStyle = MENU_STYLE,
  menuClassName,
  mobileFooterContent,
  isModal = false
}: TDropdownMenuProps) => {
  const [isOpen, setIsOpen] = React.useState(false);
  const isMobile = useMediaQuery(ScreenSizeQueries.xsOnly);

  return (
    <RadixDropdownMenu.Root
      modal={isMobile ? true : isModal}
      open={isOpen}
      onOpenChange={value => setIsOpen(value)}
    >
      <RadixDropdownMenu.Trigger asChild onPointerDown={() => setIsOpen(v => !v)}>
        {button({ isOpen })}
      </RadixDropdownMenu.Trigger>
      <RadixDropdownMenu.Portal>
        <RadixDropdownMenu.Content align={align} asChild>
          <MenuDrawer onClose={() => setIsOpen(false)} isOpen={isOpen}>
            <MenuContent
              menuStyle={menuStyle}
              wrapperClassName={menuClassName}
              mobileFooterContent={mobileFooterContent}
              onClose={() => setIsOpen(false)}
            >
              {items?.map((item, i) => {
                if ('separator' in item)
                  return (
                    <RadixDropdownMenu.Separator asChild key={i}>
                      <div className="my-2 h-px bg-gray-light max-sm:hidden" />
                    </RadixDropdownMenu.Separator>
                  );

                const { key: itemKey, ...menuItemProps } = item;

                return (
                  <MenuItem
                    menuStyle={menuStyle}
                    key={itemKey || i.toString()}
                    Element={RadixDropdownMenu.Item}
                    {...menuItemProps}
                  />
                );
              })}
            </MenuContent>
          </MenuDrawer>
        </RadixDropdownMenu.Content>
      </RadixDropdownMenu.Portal>
    </RadixDropdownMenu.Root>
  );
};

export default DropdownMenu;
